import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  BellIcon,
  CalendarDaysIcon,
  ChatBubbleLeftIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  UserPlusIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/16/solid';
import { Badge } from '../common/Catalyst/badge';
import { ReservationStatusIcon } from '../reservation/ReservationStatusIcon';
import { SeverityGlyph, StatusIcon } from '../violation/linear/StatusIcon';
import { WorkOrderStatusIcon } from '../workorder/linear/WorkOrderStatusIcon';
import { formatRelativeTime } from '../utils/dateUtils';
import { ActivityItem, ActivityKind } from './homeMeta';

const KIND: Record<ActivityKind, { icon: typeof BellIcon; label: string }> = {
  violation: { icon: ExclamationTriangleIcon, label: 'Violation' },
  workOrder: { icon: WrenchScrewdriverIcon, label: 'Work order' },
  comment: { icon: ChatBubbleLeftIcon, label: 'Comment' },
  notification: { icon: BellIcon, label: 'Notification' },
  communication: { icon: EnvelopeIcon, label: 'Communication' },
  reservation: { icon: CalendarDaysIcon, label: 'Reservation' },
  home: { icon: HomeIcon, label: 'Home' },
  member: { icon: UserPlusIcon, label: 'Member' },
};

/**
 * The row's lead glyphs, as the record's own list draws them: severity
 * (or priority) bars and the coloured status icon for violations and work
 * orders, the status icon for a booking, and otherwise what the row is.
 */
const Lead: React.FC<{ item: ActivityItem }> = ({ item }) => {
  const { icon: Icon, label } = KIND[item.kind];
  const status = item.status ?? '';
  switch (item.kind) {
    case 'violation':
      return (
        <>
          <SeverityGlyph severity={item.severity ?? ''} />
          <StatusIcon status={status} />
        </>
      );
    case 'workOrder':
      return (
        <>
          <SeverityGlyph severity={item.severity ?? ''} />
          <WorkOrderStatusIcon status={status} />
        </>
      );
    case 'reservation':
      return (
        <>
          <Icon className="h-3.5 w-3.5 shrink-0 text-fg-muted" aria-label={label} />
          <ReservationStatusIcon status={status} />
        </>
      );
    default:
      return (
        <>
          <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <Icon
            className={clsx(
              'h-3.5 w-3.5 shrink-0',
              item.unread ? 'text-accent' : 'text-fg-muted'
            )}
            aria-label={label}
          />
        </>
      );
  }
};

/**
 * One line of Home (#670): what it is, what happened and when, and where
 * it is. The whole row opens the record. Urgency and a comment's thread
 * are coloured as the record's own views colour them.
 */
export const ActivityRow: React.FC<{
  item: ActivityItem;
  now: Date;
  /** Show a comment's thread badge — only to those who post internal notes, as the thread does. */
  showThreads: boolean;
}> = ({ item, now, showThreads }) => {
  const when = formatRelativeTime(new Date(item.at).toISOString(), now);
  return (
    <li>
      <Link
        to={item.path}
        className="flex items-start gap-3 px-4 py-2.5 text-[13px] hover:bg-fg/[0.03] focus:outline-none focus-visible:bg-fg/5"
      >
        <span className="mt-0.5 flex shrink-0 items-center gap-1.5">
          <Lead item={item} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            {item.unread && (
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
                aria-label="Unread"
              />
            )}
            <span className="truncate text-fg">{item.title}</span>
          </span>
          {(item.detail || (showThreads && item.thread)) && (
            // The thread badge leads the second line rather than joining the
            // right-hand column, where it squeezed the title in a narrow panel.
            <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[12px] text-fg-muted">
              {showThreads && item.thread && (
                <Badge color={item.thread.color} className="shrink-0">
                  {item.thread.label}
                </Badge>
              )}
              {item.detail && <span className="truncate">{item.detail}</span>}
            </span>
          )}
        </span>
        <span className="flex shrink-0 flex-col items-end gap-0.5 text-[12px] text-fg-muted">
          <span className="flex items-center gap-1.5">
            {item.overdue && <Badge color="red">Overdue</Badge>}
            {item.badge && <Badge color={item.badge.color}>{item.badge.label}</Badge>}
            <span className="text-fg">{item.verb}</span>
            <span aria-hidden>·</span>
            <time dateTime={new Date(item.at).toISOString()}>{when}</time>
          </span>
          {item.hoaName && (
            <span className="max-w-[12rem] truncate">{item.hoaName}</span>
          )}
        </span>
      </Link>
    </li>
  );
};

export default ActivityRow;
